import React, {useState} from 'react'
import {Box} from '../../webui/Box'
import {Button} from '../../webui/Button'
import {Container} from '../../webui/Container'
import {CheckboxWithLabel, CheckboxWithLabelProps} from '../../webui/inputs/CheckboxWithLabel'
import {MultiSearchSelect, MultiSearchSelectProps, Option, SearchSelect, SearchSelectProps} from '../../webui/inputs/SearchSelect'
import {Paper} from '../../webui/Paper'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'
import {useAllBoxesForSelect} from '../box/gql/useAllBoxesForSelect'
import {sortBoxes} from '../box/utils/sortBoxes'
import {useAllOrganisations} from '../organisation/gql/useAllOrganisations'
import {useAllProducts} from '../product/gql/useAllProducts'
import {getFilteredBoxesWithOrganisationBoxes} from './filter/utils/getFilteredBoxesWithOrganisationBoxes'
import {StatisticsProductsResults} from './products/StatisticsProductsResults'

interface Props {}

export type AppliedProductsFilter = {
  selectedBoxes: Option[]
  onlyActiveBoxes: boolean
}

export const StatisticsProductsPage: React.FC<Props> = () => {
  // Lightweight list for dropdown options (id + name only).
  const {data: boxesForSelect, loading: boxesForSelectLoading} = useAllBoxesForSelect()

  const {data: allOrganisations, loading: allOrganisationsLoading} = useAllOrganisations()
  const {data: allProducts, loading: allProductsLoading} = useAllProducts()

  const [selectedBoxes, setSelectedBoxes] = useState<Option[]>([])
  const [onlyActiveBoxes, setOnlyActiveBoxes] = useState<boolean>(true)
  const [appliedFilter, setAppliedFilter] = useState<AppliedProductsFilter | null>(null)

  const onBoxesChange: MultiSearchSelectProps['onChange'] = (value) => {
    setSelectedBoxes(value.slice())
  }

  const onOrganisationChange: SearchSelectProps['onChange'] = (value) => {
    if (value) {
      const organisation = allOrganisations.find((org) => org.id === value.value)
      if (organisation) {
        setSelectedBoxes(getFilteredBoxesWithOrganisationBoxes(selectedBoxes, organisation))
      }
    }
  }

  const onOnlyActiveBoxesChange: CheckboxWithLabelProps['onChange'] = (value) => {
    setOnlyActiveBoxes(value)
  }

  const onApplyFilter = () => {
    setAppliedFilter({selectedBoxes, onlyActiveBoxes})
  }

  if (boxesForSelectLoading || allOrganisationsLoading || allProductsLoading) {
    return <LoadingSpinnerContainer />
  }

  return (
    <Container>
      <Paper>
        <Box p={2} display={'flex'} flexDirection={'row'} alignItems={'flex-end'}>
          <MultiSearchSelect
            label={'Boxes'}
            onChange={onBoxesChange}
            options={sortBoxes(boxesForSelect).map((box) => ({
              label: box.name,
              value: box.id,
            }))}
            value={selectedBoxes}
          />
          <Box ml={2}>
            <CheckboxWithLabel label={'Only active boxes'} labelPlacement={'start'} onChange={onOnlyActiveBoxesChange} value={onlyActiveBoxes} />
          </Box>
          <Box ml={2} />
          <SearchSelect
            label={'Organisations'}
            onChange={onOrganisationChange}
            options={allOrganisations.map((organisation) => ({
              label: organisation.name,
              value: organisation.id,
            }))}
            value={null}
          />
          <Box ml={2} />
          <Button onClick={onApplyFilter}>Använd filter</Button>
        </Box>
      </Paper>

      <Box mt={2} />

      {appliedFilter && <StatisticsProductsResults appliedFilter={appliedFilter} allProducts={allProducts} />}
    </Container>
  )
}
